<script>
	import thumb_Vpxh_ReHJmA from '$lib/Components/Youtube/images/Vpxh-ReHJmA.jpg?w=610';

	import Bubble from '$lib/Components/Layout/Bubble/Bubble.svelte';
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import Youtube from '$lib/Components/Youtube/Youtube.svelte';
	import GetAFreeEstimate from '$lib/PageSections/GetAFreeEstimate/GetAFreeEstimate.svelte';

	import soundproofing_windows from './case-study-soundproofing-windows-in-nyc-002.jpg';
	import soundproofing2 from './resources-soundproofing-home-subnavB-003.png';
	import howCanSoundproofMyHomeIcon from './Sound-Page-1.png';
	import Sound_Page_1B from './Sound-Page-1B.png';
</script>

<Section backgroundColor="white">
	<h2>Home Soundproofing Tips</h2>
	<p>
		Home soundproofing becomes more necessary as the world around us becomes
		louder. From traffic noise to yard equipment, noise disturbs the sense of
		peace in your home and makes it hard to sleep. Fortunately, good solutions
		for soundproofing homes exist: from air sealing and wall insulation to
		window inserts and acoustic panels. Such techniques for how to
		<a href="/solutions/window-noise-reduction">soundproof</a>
		a home can make your space quiet and comfortable. (So the nearby train doesn’t
		sound like it’s in your living room!)
	</p>
	<Bubble title="How can I soundproof my home?">
		<ol
			class="
     marker-number marker-circle
     [&_p]:w-full
   "
		>
			<li>
				<a href="#outside">
					<p>Soundproof your home from outside noise</p>
				</a>
			</li>
			<li>
				<a href="#inside">
					<p>Soundproof a home from inside noise</p>
				</a>
			</li>
			<li>
				<a href="#experts">
					<p>Get help from home soundproofing experts</p>
				</a>
			</li>
			<li>
				<a href="#solutions">
					<p>Home Soundproofing Solutions</p>
				</a>
			</li>
			<li>
				<a href="#expectations">
					<p>Set your soundproofing expectations</p>
				</a>
			</li>
			<li>
				<a href="#project">
					<p>Start your home soundproofing project</p>
				</a>
			</li>
		</ol>
	</Bubble>
	<Image
		src={howCanSoundproofMyHomeIcon}
		alt="home soundproofing and sources of sound"
	/>
</Section>

<Section backgroundColor="white">
	<h2>Why Noise Happens and How to Stop It</h2>
	<p>
		Wherever air goes, noise follows. Sound travels by airwave, so unless you
		can live without air, you will be living with some noise. Think of
		soundproofing (blocking noise) like blocking water, if there are openings,
		noise will find a way through. There are two basic
		<a href="/resources/soundproofing-windows">types of soundproofing</a>
		: sound reduction and sound absorption. Sound reduction stops noise from entering
		your home and sound absorption stops noise from echoing and reverberating inside
		your home.
	</p>
	<p>
		You can measure how effective a soundproofing product is by using STC
		ratings. Using Sound Transmission Class ratings (STC ratings) is a way to
		compare products for their ability to stop the transmission of sound. Always
		keep in mind, the best
		<a href="/resources/blog/most-effective-soundproofing-materials">
			soundproofing materials
		</a>
		depend on how and what you are using them for.
	</p>
	<Image
		src={soundproofing_windows}
		alt="Soundproofing a house from traffic noise."
	/>
</Section>

<Section backgroundColor="white" id="outside">
	<h2>1. How to Soundproof a Home from Outside Noise</h2>
	<p>
		Noise can penetrate doors, walls, ceilings and other openings in your house.
		How does this work? Sound is energy that’s created when something vibrates.
		Strum a guitar and the vibrating strings make the surrounding air molecules
		vibrate too. That sound energy then travels outward and penetrates solids.
	</p>
	<p>
		Insulated walls block sound better than uninsulated walls. A solid wood door
		transmits less sound than a hollow one. One key principle: if air can
		penetrate through the cracks in your home, sound can too. So effective seals
		and gaskets around doors and windows are important noise barriers.
	</p>
	<p>
		Windows are typically the primary entry point. Noise travels easily through
		glass. It also enters through the many cracks in a window frame (as do cold
		drafts.)
		<a href="/resources/blog/soundproof-windows-home-depot">
			Insulating your windows against sound
		</a>
		isn’t as difficult as you may think. Was your home built before 1950? There’s
		a good chance you have single-pane windows, which allow more noise to enter your
		space than new double-panes. But even new double-pane windows can let in a lot
		of noise and make home soundproofing difficult. Don’t worry:
		<a href="/solutions/window-noise-reduction">window noise reduction</a>
		is within reach.
	</p>
</Section>

<Section backgroundColor="white" id="inside">
	<h2>2. How to Soundproof a Home from Inside Noise</h2>
	<p>
		Noise coming from inside your own home can bounce and reverberate against
		walls, floors, and windows. Home soundproofing can help with this as well.
		Reducing hard surfaces by adding soft furniture, area rugs, and hanging
		things on walls will reduce echoing. Indow inserts are made of acrylic which
		reverberates less than glass, and absorbs more sound.
	</p>
	<Image src={Sound_Page_1B} alt="Home soundproofing frustrations." />
</Section>

<Section backgroundColor="white" id="experts">
	<h2>3. Get Help From Home Soundproofing Experts</h2>
	<p>
		Now, what if your noise problem stems from neighbors upstairs or downstairs?
		How can you soundproof a home when you share walls with the noise? It can
		involve building drop ceilings and floating floors. If you have the budget,
		consider hiring an
		<a
			href="https://ncac.com/what-sets-an-expert-apart"
			target="_blank"
			rel="noreferrer"
		>
			acoustical consultant
		</a>
		to assess your noise situation. A good resource is the
		<a href="https://ncac.com/directory" target="_blank" rel="noreferrer">
			National Council of Acoustical Consultants
		</a>
		.
	</p>
</Section>

<Section backgroundColor="white" id="solutions">
	<h2>4. Home Soundproofing Solutions</h2>
	<p>
		If outside noise is your main problem, you can
		<a href="/resources/case-studies/street-noise-reduction">
			soundproof your house
		</a>
		or apartment several ways. Caulk cracks in your window frames, install heavy,
		sound-damping curtains or use window inserts to reduce 50 to 70%* of the noise
		as well as block drafts to lower energy bills. Insulating your walls and ceiling
		also helps. Once you reduce the sound coming through the windows of your house
		or apartment, absorb noise in the room to improve acoustics, with everything from
		upholstered furniture to
		<a href="/solutions/window-noise-reduction">acoustic panels</a>
		.
	</p>
	<p>
		Keep in mind not all noise is the same. Insulating your windows against
		sound will work best for higher frequency noises like honking horns, sirens
		and squeaking brakes. But lower frequency noises like the thumping bass of a
		passing car or the rumble of a powerful motorcycle can penetrate walls.
		Lower frequency noise damping is more tricky.
	</p>
	<h3>How to Soundproof a Room From Outside Noise</h3>
	<p>
		Adding insulation to your walls and ceilings will lower the transmission of
		sound from the outside of your home to the inside. Often homeowners will fix
		cracks in windows and doors, but not examine the insulation of the
		surrounding walls. The best way to answer how to soundproof a room from
		outside noise is to look at it as a whole. Are all parts well insulated so
		that noise has no way in? The attic is especially overlooked.
	</p>
	<h3>Watch: How to Soundproof Your Home Using Window Inserts</h3>
	<Youtube posterImageSrc={thumb_Vpxh_ReHJmA} videoId="Vpxh-ReHJmA" />
	<p>
		Watch this video to see window inserts in action. We perform a noise test,
		placing a phone making siren noises in between the window and the insert,
		sealing it in place. Window inserts work well for soundproofing homes
		against both outside noise and inside noise. The airtight seal they create
		reduces outside noise, and the acrylic panel is fantastic at absorbing and
		reducing inside noise. Easy to install, they are perfect for
		<a href="/resources/case-studies/street-noise-reduction">
			soundproofing a house from traffic noise
		</a>
		.
	</p>
	<h3>Soundproofing Homes with Upholstery & Rugs</h3>
	<p>
		Home soundproofing can sometimes come down to decor. Cushioned furniture,
		thick rugs, large drapery all help with soundproofing homes. Many modern
		styles of furniture – sleek, slender, without cushions – will reflect noise
		and add to echoes in your home. Cushioned couches over hard chairs, drapes
		over smaller window attachments, and even hanging wall art can all help
		absorb noise that is either created in your home or penetrates your home.
	</p>
	<p>
		Set your expectations: you may not be able to completely soundproof your
		home. But you can reduce the noise to make it a more comfortable place to
		relax.
	</p>
</Section>

<Section backgroundColor="white" id="expectations">
	<h2>5. Set Your Home Soundproofing Expectations</h2>
	<p>
		Keep in mind not all noise is the same. Insulating your windows against
		sound will work best for higher frequency noises like honking horns, sirens
		and squeaking brakes. But lower frequency noises like the thumping bass of a
		passing car or the rumble of a powerful motorcycle can penetrate walls.
		Lower frequency noise damping is more tricky.
	</p>
	<p>
		Set your expectations: you may not be able to completely soundproof your
		home. But you can reduce the noise to make it a more comfortable place to
		relax. Visit our
		<a href="/resources/stc-rating">STC rating page</a>
		to learn more about sound ratings and their limitations.
	</p>
</Section>

<Section backgroundColor="white" id="project">
	<h2>6. How to Soundproof a Home</h2>
	<Bubble>
		<h3>
			Use this quick list to get started with your home soundproofing project:
		</h3>
		<ul>
			<li>Caulk cracks in window frames</li>
			<li>Install sound-damping curtains</li>
			<li>Install window inserts or storm windows</li>
			<li>
				Absorb noise with upholstered furniture &
				<a href="/solutions/window-noise-reduction">acoustic panels</a>
			</li>
		</ul>
	</Bubble>
</Section>

<Section backgroundColor="white">
	<h2>Learn more about how to soundproof a home here.</h2>

	<a href="/resources/soundproofing-windows" class="mb-4">
		<Image
			class="w-44"
			src={soundproofing2}
			alt="Home soundproofing outside noise."
		/>
		<p class="text-indow-blue">Soundproofing Windows: The Basics</p>
	</a>

	<p class="text-xs italic">
		*We want to ensure our inserts help solve your noise problem. Overall noise
		reduction depends on the amount of noise coming through your walls,
		ceilings, floors, doors, and existing windows. Noise reduction will be less
		when placed over double-pane windows. Read our full
		<a href="/noise-disclaimer">Noise Reduction Sheet</a>
		to learn more.
	</p>
</Section>

<GetAFreeEstimate title="Let Us Help You" />
